import * as THREE from 'three';
import { WebglScene } from "../webgl/scene";
import { WebglFurniture } from '../webgl/furniture';
import { Coating, Commands, ComponentConstants, Events, eventsManager, FloorCoatingArea, SceneConstants } from '@rhinov/savane-js';

export class SmartContentHandler {

    private divWebgl: HTMLElement;
    private scene: WebglScene;

    constructor(divWebgl: HTMLElement, scene: WebglScene) {
        this.divWebgl = divWebgl;
        this.scene = scene;

        eventsManager.instance.addListener(Events.COMMAND_EXECUTED, (event: any) => {
            this.ExecuteCommand(event);
        });
    }

    ExecuteCommand = (event: any) : void => {
        // Depending on the command name (see Command.js from CocosPlanModule/src/cocos/scripts/command)
        switch (event.userData.name) {
            case Commands.CommandEnum.EditEntityCommand:
                // Get webGL entity
                let webglEntity = this.scene.getPlanEntity(event.userData.datas.id);
                // If it exists
                if (webglEntity !== null) {
                    //  We will go up the hierarchy
                    let rootArrangementEntity = webglEntity.entity;

                    // Until we find the root arrangement
                    if (rootArrangementEntity.parent) {
                        while (rootArrangementEntity.parent &&
                            (rootArrangementEntity.parent.entityType === SceneConstants.EntityType.ArrangementGroup || rootArrangementEntity.parent.entityType === SceneConstants.EntityType.ArrangementObject ||
                            rootArrangementEntity.parent.entityType === SceneConstants.EntityType.GeometryGroup)) {
                            rootArrangementEntity = rootArrangementEntity.parent;
                        }
                    }

                    // And we update the whole tree underneath so all positions are updated
                    this.scene.updateTree(rootArrangementEntity);
                    this.scene.render();
                }
                break;
        }
    }

    SelectArrangement = (point: {x: number, y: number}) : void => {
        let mousePosition = new THREE.Vector2(point.x, point.y);
        let camera = this.scene.getActiveGLCamera() as THREE.PerspectiveCamera;
        this.scene.rayCaster.setFromCamera(mousePosition, camera);
        let intersects = this.scene.rayCaster.intersectObjects(this.scene.threeScene.children, true);
        intersects = this.scene.removeGizmoFromIntersectionResults(intersects, true);

        if (intersects.length === 0) {
            return;
        }

        // Try to get the savane entity id stored into the userdata of the object
        let id = intersects[0].object.parent!.userData.id;

        // This is for objects that do not have VLP they'll only have the default mesh instead of the vlp itself, do not search for parent then
        if (id === undefined) {
            id = intersects[0].object.userData.id;
        }

        if (id !== undefined) {
            let node = this.scene.getPlanEntity(id) as WebglFurniture;
            eventsManager.instance.dispatch(Events.SELECTED_ARRANGEMENT, { entity: node.entity, point: point });
        }
    }

    SelectCoating = (point: {x: number, y: number}) : boolean => {
        let mousePosition = new THREE.Vector2(point.x, point.y);
        let camera = this.scene.getActiveGLCamera() as THREE.PerspectiveCamera;
        this.scene.rayCaster.setFromCamera(mousePosition, camera);
        let intersects = this.scene.rayCaster.intersectObjects(this.scene.threeScene.children, true);
        intersects = this.scene.removeGizmoFromIntersectionResults(intersects, true);

        if (intersects.length === 0) {
            return false;
        }

        let intersect = intersects[0];
        // hull entity
        let entity = null;
        let childs: any[] = [];
        if (!entity && this.scene.floorGeneratorHull) {
            entity = this.scene.floorGeneratorHull.getEntityFromChild(intersect.object);
            if (entity) {
                childs = this.scene.floorGeneratorHull.getChildsFromId(entity.id);
            }
        }
        if (!entity && this.scene.dynamicHull) {
            entity = this.scene.dynamicHull.getEntityFromChild(intersect.object);
            if (entity) {
                childs = this.scene.dynamicHull.getChildsFromId(entity.id);
            }
        }
        if (!entity && this.scene.staticHull) {
            entity = this.scene.staticHull.getEntityFromChild(intersect.object);
            if (entity) {
                childs = this.scene.staticHull.getChildsFromId(entity.id);
            }
        }

        let result = false;
        if (entity && (entity.isWallEntity() || entity.isRoomEntity() || entity.isFloorEntity() || entity.isGeometryPrimitiveEntity())) {
            let coatingArea = intersect.object.name.startsWith("CoatingArea");

            // coating
            let hangType = intersect.object.name.indexOf("Direct") !== -1 ? Coating.HangType.wallDirect : Coating.HangType.wallUndirect;
            if (entity.isWallEntity()) {
                if (intersect.object.name.indexOf("Slope") !== -1) {
                    if (intersect.object.name.indexOf("Direct") === -1) {
                        hangType = Coating.HangType.slopeUndirect;
                    }
                    else {
                        hangType = Coating.HangType.slopeDirect;
                    }
                }
            } else if (entity.isRoomEntity() || entity.isFloorEntity()) {
                hangType = intersect.object.name.indexOf("axo") !== -1 ? Coating.HangType.ceiling : Coating.HangType.floor;
            } else if (entity.isGeometryPrimitiveEntity()) {
                hangType = Coating.HangType.technicalElement;
                let primGeoParse = intersect.object.name.split('_');
                (intersect.object as any).coatingId = primGeoParse[2];
            }

            if (coatingArea) {
                let coatingAreaParse = intersect.object.name.split('_');
                let coatingAreaNb = Number(coatingAreaParse[2]) - 1;
                let coatings = entity.getComponents(ComponentConstants.ComponentType.CoatingArea);
                eventsManager.instance.dispatch(Events.SELECTED_COATING, { entity: entity, coating: coatings[coatingAreaNb], point: point });
                result = true;
            }
            else if ((intersect.object as any).coatingId) {
                //Check coating
                let coatings: Array<Coating | FloorCoatingArea> = [];
                if (coatingArea == false) {
                    coatings = coatings.concat(entity.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating | FloorCoatingArea>);
                }
                if (coatings.length == 0) {
                    coatings = entity.getComponents(ComponentConstants.ComponentType.FloorCoatingArea) as Array<Coating | FloorCoatingArea>;
                }
                if ((coatings !== null) && (coatings.length > 0)) {
                    for (let i = 0; i < coatings.length; i++) {
                        if ((intersect.object as any).coatingId !== coatings[i].coatingId) {
                            continue;
                        }
                        if (coatings[i].hangType !== hangType) {
                            continue;
                        }
                        
                        eventsManager.instance.dispatch(Events.SELECTED_COATING, { entity: entity, coating: coatings[i], point: point });
                        result = true;
                        break;
                    }
                }
            }
        }
        if (this.scene.outlinePass && result) {
            this.scene.outlinePass.selectedObjects = childs;
        }
        return result;
    }

    HandleMouseDown = (event: MouseEvent) : void => {
        if (event.button === 0) {
            let coatingSelected = this.SelectCoating({
                x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
            });
            if (!coatingSelected) {
                this.SelectArrangement({
                    x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                    y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                });
            }
        }
    }

    Resize = () : void => {
        this.scene.renderer.setSize(this.divWebgl.parentElement!.clientWidth, this.divWebgl.parentElement!.clientHeight);
    }

    UpdateCamera = () : void => {
        if (this.divWebgl.parentNode) {
            (this.divWebgl.parentNode as any).style.height = window.innerHeight + "px";
        }
        this.scene.updateCamera(this.scene.camera!.entity.id);
        if (this.divWebgl.parentNode) {
            (this.divWebgl.parentNode as any).style.height = this.divWebgl.clientHeight + "px";
        }
    }
}
